import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  CreditCard, Building2, User, MapPin, Calendar, 
  DollarSign, CheckCircle, AlertCircle, Loader2, 
  Phone, Mail, FileText, Shield
} from 'lucide-react';

// Use a separate axios instance without auth for public routes
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://suddyflow-api-cugygabvhfg6dvfj.eastus2-01.azurewebsites.net';

const PayInvoice = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const canceled = searchParams.get('canceled');

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInvoice();
  }, [token]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/api/pay/invoice/${token}`);
      setInvoice(response.data);
    } catch (err) {
      console.error('Error loading invoice:', err);
      if (err.response?.status === 404) {
        setError('Invoice not found. Please check the link and try again.');
      } else {
        setError('Unable to load invoice. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    try {
      setProcessing(true);
      setError(null);
      const response = await axios.post(`${API_BASE_URL}/api/pay/invoice/${token}/checkout`);
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.response?.data?.error || 'Unable to start payment. Please try again.');
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Invoice</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={loadInvoice}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Already paid state
  if (invoice?.isPaid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Paid</h1>
          <p className="text-gray-600 mb-4">{invoice.message}</p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-500">Invoice</p>
            <p className="font-semibold text-gray-900">{invoice.invoiceNumber}</p>
            {invoice.paidDate && (
              <>
                <p className="text-sm text-gray-500 mt-2">Paid On</p>
                <p className="font-semibold text-gray-900">{formatDate(invoice.paidDate)}</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Canceled Notice */}
        {canceled && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Payment Canceled</p>
              <p className="text-sm text-amber-700">
                Your payment was not processed. You can try again when you're ready.
              </p>
            </div>
          </div>
        )}

        {/* Error Notice */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Invoice Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">{invoice?.businessName}</h1>
                <p className="text-blue-100 text-sm">Invoice {invoice?.invoiceNumber}</p>
              </div>
            </div>
            
            <div className="mt-6">
              <p className="text-blue-100 text-sm mb-1">Amount Due</p>
              <p className="text-3xl sm:text-4xl font-bold">{formatCurrency(invoice?.amountDue)}</p>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="p-6 space-y-6">
            {/* Customer & Job Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Billed To</p>
                  <p className="font-medium text-gray-900">{invoice?.customerName}</p>
                </div>
              </div>
              
              {invoice?.jobAddress && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Service Address</p>
                    <p className="font-medium text-gray-900">{invoice?.jobAddress}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Invoice Date</p>
                  <p className="font-medium text-gray-900">{formatDate(invoice?.invoiceDate)}</p>
                </div>
              </div>
              
              {invoice?.dueDate && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Due Date</p>
                    <p className={`font-medium ${
                      new Date(invoice.dueDate) < new Date() ? 'text-red-600' : 'text-gray-900'
                    }`}>
                      {formatDate(invoice?.dueDate)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Amount Breakdown */}
            <div className="border-t border-gray-200 pt-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">{formatCurrency(invoice?.subTotal)}</span>
                </div>
                {invoice?.taxAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax</span>
                    <span className="text-gray-900">{formatCurrency(invoice?.taxAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total</span>
                  <span className="text-gray-900">{formatCurrency(invoice?.totalAmount)}</span>
                </div>
                {invoice?.amountPaid > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Amount Paid</span>
                    <span className="text-green-600">-{formatCurrency(invoice?.amountPaid)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="font-semibold text-gray-900">Amount Due</span>
                  <span className="font-bold text-xl text-gray-900">{formatCurrency(invoice?.amountDue)}</span>
                </div>
              </div>
            </div>

            {/* Pay Button */}
            {invoice?.canPayOnline ? (
              <div className="pt-4">
                <button
                  onClick={handlePayNow}
                  disabled={processing}
                  className="w-full py-4 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 text-lg"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Connecting to Stripe...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Pay {formatCurrency(invoice?.amountDue)} Now
                    </>
                  )}
                </button>
                
                {/* Security Notice */}
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Shield className="w-4 h-4" />
                  <span>Secure payment powered by Stripe</span>
                </div>
              </div>
            ) : (
              <div className="pt-4">
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-gray-600 mb-4">{invoice?.message || 'Online payment is not available for this invoice.'}</p>
                  {(invoice?.businessPhone || invoice?.businessEmail) && (
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
                      {invoice?.businessPhone && (
                        <a 
                          href={`tel:${invoice.businessPhone}`}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                        >
                          <Phone className="w-4 h-4" />
                          {invoice.businessPhone}
                        </a>
                      )}
                      {invoice?.businessEmail && (
                        <a 
                          href={`mailto:${invoice.businessEmail}`}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                        >
                          <Mail className="w-4 h-4" />
                          {invoice.businessEmail}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Powered by <span className="font-medium text-gray-700">SuddyFlow</span></p>
        </div>
      </div>
    </div>
  );
};

export default PayInvoice;